import { useState, useCallback } from 'react';
import { toast } from '../components/toast';
import { useLoadingState } from './useLoadingState';
import {
  getEmployeeLeaves,
  createEmployeeLeave,
  updateEmployeeLeave,
  deleteEmployeeLeave,
  getEmployeeLeavesSettings,
  createEmployeeLeaveSettings,
  updateEmployeeLeaveSettings,
  deleteEmployeeLeaveSettings,
} from '../services/api';

export const useLeaveOperations = () => {
  const { isLoading, withLoading } = useLoadingState({ minLoadingTime: 800 });
  const { isLoading: isSettingsLoading, withLoading: withSettingsLoading } = useLoadingState({ minLoadingTime: 800 });

  const [leaves, setLeaves] = useState([]);
  const [stats, setStats] = useState({
    total_leaves: 0,
    pending_leaves: 0,
    approved_leaves: 0,
    rejected_leaves: 0,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [filterOptions, setFilterOptions] = useState({
    employees: [],
    statuses: [],
    leave_types: [],
  });
  const [allEmployees, setAllEmployees] = useState([]);

  const [leaveSettings, setLeaveSettings] = useState([]);
  const [settingsTotalCount, setSettingsTotalCount] = useState(0);

  const loadFilterOptions = useCallback(async () => {
    try {
      setAllEmployees((prevEmployees) => {
        if (filterOptions.employees && filterOptions.employees.length > 0) {
          return filterOptions.employees;
        }
        return prevEmployees;
      });
    } catch (error) {
      console.error('Error processing filter options:', error);
    }
  }, [filterOptions.employees]);

  const loadLeaves = useCallback(
    async (page, rowsPerPage, searchTerm, statusFilter, employeeFilter, leaveTypeFilter, startDate, endDate) => {
      await withLoading(async () => {
        try {
          const response = await getEmployeeLeaves(
            page + 1,
            rowsPerPage,
            searchTerm,
            statusFilter,
            employeeFilter,
            leaveTypeFilter,
            startDate,
            endDate
          );

          if (response.data.results) {
            setLeaves(response.data.results);
            setTotalCount(response.data.pagination_info?.count || 0);
            if (response.data.stats) {
              setStats(response.data.stats);
            }
            if (response.data.filter_options) {
              setFilterOptions(response.data.filter_options);
              if (response.data.filter_options.employees) {
                setAllEmployees(response.data.filter_options.employees);
              }
            }
          }
        } catch (error) {
          console.error('Error loading leaves:', error);
          toast.error('Failed to load employee leaves. Please try again.');
        }
      });
    },
    [withLoading]
  );

  const loadLeaveSettings = useCallback(
    async (page, rowsPerPage, searchTerm, employeeFilter) => {
      await withSettingsLoading(async () => {
        try {
          const response = await getEmployeeLeavesSettings(page + 1, rowsPerPage, searchTerm, employeeFilter);

          if (response.data.results) {
            setLeaveSettings(response.data.results);
            setSettingsTotalCount(response.data.pagination_info?.count || response.data.count || 0);

            if (response.data.filter_options && response.data.filter_options.employees) {
              setAllEmployees(response.data.filter_options.employees);
              setFilterOptions((prev) => ({
                ...prev,
                employees: response.data.filter_options.employees,
              }));
            }
          }
        } catch (error) {
          console.error('Error loading leave settings:', error);
          toast.error('Failed to load leave settings. Please try again.');
        }
      });
    },
    [withSettingsLoading]
  );

  const createLeaveRecord = useCallback(async (leaveData) => {
    try {
      await createEmployeeLeave(leaveData);
      toast.success('Employee leave created successfully!');
      return true;
    } catch (error) {
      console.error('Error creating leave:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to create employee leave. Please try again.');
      }
      return false;
    }
  }, []);

  const updateLeaveRecord = useCallback(async (leaveId, leaveData) => {
    try {
      await updateEmployeeLeave(leaveId, leaveData);
      toast.success('Employee leave updated successfully!');
      return true;
    } catch (error) {
      console.error('Error updating leave:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to update employee leave. Please try again.');
      }
      return false;
    }
  }, []);

  const deleteLeaveRecord = useCallback(async (leaveId) => {
    try {
      await deleteEmployeeLeave(leaveId);
      toast.success('Employee leave deleted successfully!');
      return true;
    } catch (error) {
      console.error('Error deleting leave:', error);
      toast.error('Failed to delete employee leave. Please try again.');
      return false;
    }
  }, []);

  const createLeaveSettingsRecord = useCallback(async (settingsData) => {
    try {
      await createEmployeeLeaveSettings(settingsData);
      toast.success('Employee leave settings created successfully!');
      return true;
    } catch (error) {
      console.error('Error creating leave settings:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to create leave settings. Please try again.');
      }
      return false;
    }
  }, []);

  const updateLeaveSettingsRecord = useCallback(async (settingsId, settingsData) => {
    try {
      await updateEmployeeLeaveSettings(settingsId, settingsData);
      toast.success('Employee leave settings updated successfully!');
      return true;
    } catch (error) {
      console.error('Error updating leave settings:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to update leave settings. Please try again.');
      }
      return false;
    }
  }, []);

  const deleteLeaveSettingsRecord = useCallback(async (settingsId) => {
    try {
      await deleteEmployeeLeaveSettings(settingsId);
      toast.success('Employee leave settings deleted successfully!');
      return true;
    } catch (error) {
      console.error('Error deleting leave settings:', error);
      toast.error('Failed to delete leave settings. Please try again.');
      return false;
    }
  }, []);

  return {
    isLoading,
    leaves,
    stats,
    totalCount,
    filterOptions,
    allEmployees,
    loadFilterOptions,
    loadLeaves,
    createLeaveRecord,
    updateLeaveRecord,
    deleteLeaveRecord,
    isSettingsLoading,
    leaveSettings,
    settingsTotalCount,
    loadLeaveSettings,
    createLeaveSettingsRecord,
    updateLeaveSettingsRecord,
    deleteLeaveSettingsRecord,
  };
};
